import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Tabs,
  Tab,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Description,
  People,
  CheckBox,
  NotificationAdd,
  Comment,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";

import WhoWasInvolved from "../IncidentComponent/WhoWasInvolved";
import WhoWasInformed from "../IncidentComponent/WhoWasInformed";
import WhoWitnessed from "../IncidentComponent/WhoWitnessed";
import IncidentDetails from "../IncidentComponent/IncidentDetails";
import AdditionalChecks from "../IncidentComponent/AdditionalChecks";
import RiddorInfo from "../IncidentComponent/RiddorInfo";
import RiskNotification from "../IncidentComponent/RiskNotification";
import SafeguardingInfo from "../IncidentComponent/SafeGuardingInfo";
import InspectorateNotificationInfo from "../IncidentComponent/InspectorateNotificationInfo";
import PoliceNotificationInfo from "../IncidentComponent/PoliceNotificationInfo";
import SocialChcNotificationInfo from "../IncidentComponent/SocialChcNotificationInfo";
import CurrentStatus from "../IncidentComponent/CurrentStatus";
import Commentary from "../IncidentComponent/Commentary";
import IncidentDialogueTitle from "../IncidentComponent/IncidentDialogueTitle";

import { QK } from "../../../base/qk";
import {
  getIncidentDetails,
  updateIncident,
} from "../../../api/services/notification";

function TabPanel({ children, value, index, ...other }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`incident-tabpanel-${index}`}
      aria-labelledby={`incident-tab-${index}`}
      {...other}
      sx={{ pt: 2.5 }}
    >
      {value === index && children}
    </Box>
  );
}

function a11yProps(index) {
  return {
    id: `incident-tab-${index}`,
    "aria-controls": `incident-tabpanel-${index}`,
  };
}

const SectionWrapper = ({ children, theme }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: "12px",
      bgcolor: alpha(theme.palette.primary.main, 0.02),
      border: "1px solid #F2F4F7",
    }}
  >
    {children}
  </Box>
);

const IncidentDetailsModal = ({ open, handleClose, id }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: incidentDetails, isLoading } = useQuery(
    [QK.incidentDetails, id],
    () => getIncidentDetails(id),
    { enabled: !!id }
  );

  useEffect(() => {
    if (incidentDetails) {
      setFormData(incidentDetails);
    }
  }, [incidentDetails]);

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const response = await updateIncident(id, formData);
      if (response.status === 200 || response.status === 204) {
        enqueueSnackbar("Incident updated successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries(QK.incidentsList);
        queryClient.invalidateQueries([QK.incidentDetails, id]);
      }
    } catch (error) {
      enqueueSnackbar("Failed to update incident", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          minHeight: "85vh",
          borderRadius: "12px",
        },
      }}
    >
      <IncidentDialogueTitle
        formData={formData}
        handleFormChange={handleFormChange}
        handleClose={handleClose}
      />

      <Divider />

      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            bgcolor: "#FFFFFF",
            borderBottom: "1px solid #EAECF0",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 3,
              minHeight: 48,
              "& .MuiTab-root": {
                minHeight: 48,
                py: 1,
                fontWeight: 600,
                fontSize: "0.8rem",
                textTransform: "none",
                color: "#475467",
                gap: 0.5,
                "& .MuiSvgIcon-root": { fontSize: 16 },
                "&.Mui-selected": { color: theme.palette.primary.main },
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            <Tab
              icon={<Description />}
              iconPosition="start"
              label="Incident Details"
              {...a11yProps(0)}
            />
            <Tab
              icon={<People />}
              iconPosition="start"
              label="People & Witnesses"
              {...a11yProps(1)}
            />
            <Tab
              icon={<CheckBox />}
              iconPosition="start"
              label="Additional Checks"
              {...a11yProps(2)}
            />
            <Tab
              icon={<NotificationAdd />}
              iconPosition="start"
              label="Notification Info"
              {...a11yProps(3)}
            />
            <Tab
              icon={<Comment />}
              iconPosition="start"
              label="Status & Commentary"
              {...a11yProps(4)}
            />
          </Tabs>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, bgcolor: "#F8FAFC" }}>
          <TabPanel value={activeTab} index={0}>
            <SectionWrapper theme={theme}>
              <IncidentDetails
                formData={formData}
                handleFormChange={handleFormChange}
              />
            </SectionWrapper>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Stack spacing={2}>
              <SectionWrapper theme={theme}>
                <WhoWasInvolved
                  formData={formData}
                  onChange={handleFormChange}
                />
              </SectionWrapper>
              <SectionWrapper theme={theme}>
                <WhoWasInformed
                  formData={formData}
                  onChange={handleFormChange}
                />
              </SectionWrapper>
              <SectionWrapper theme={theme}>
                <WhoWitnessed
                  formData={formData}
                  onChange={handleFormChange}
                />
              </SectionWrapper>
            </Stack>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <SectionWrapper theme={theme}>
              <AdditionalChecks
                formData={formData}
                handleFormChange={handleFormChange}
              />
            </SectionWrapper>
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <Stack spacing={2}>
              <SectionWrapper theme={theme}>
                <RiddorInfo
                  formData={formData}
                  handleChange={handleFormChange}
                />
              </SectionWrapper>
              <SectionWrapper theme={theme}>
                <RiskNotification
                  formData={formData}
                  handleChange={handleFormChange}
                />
              </SectionWrapper>
              <SectionWrapper theme={theme}>
                <SafeguardingInfo
                  formData={formData}
                  handleChange={handleFormChange}
                />
              </SectionWrapper>
              <SectionWrapper theme={theme}>
                <InspectorateNotificationInfo
                  formData={formData}
                  handleChange={handleFormChange}
                />
              </SectionWrapper>
              <SectionWrapper theme={theme}>
                <SocialChcNotificationInfo
                  formData={formData}
                  handleChange={handleFormChange}
                />
              </SectionWrapper>
              <SectionWrapper theme={theme}>
                <PoliceNotificationInfo
                  formData={formData}
                  handleChange={handleFormChange}
                />
              </SectionWrapper>
            </Stack>
          </TabPanel>

          <TabPanel value={activeTab} index={4}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr" },
                gap: 2,
                alignItems: "start",
              }}
            >
              <SectionWrapper theme={theme}>
                <Commentary
                  formData={formData}
                  handleFormChange={handleFormChange}
                />
              </SectionWrapper>
              <SectionWrapper theme={theme}>
                <CurrentStatus
                  formData={formData}
                  handleFormChange={handleFormChange}
                />
              </SectionWrapper>
            </Box>
          </TabPanel>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#F9FAFB",
          borderTop: "1px solid #EAECF0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            color: "#94A3B8",
            fontSize: "0.72rem",
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
          Changes are saved automatically to your incident record
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button
            onClick={handleClose}
            variant="outlined"
            size="small"
            disabled={isSaving}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              borderColor: "#D0D5DD",
              color: "#344054",
              px: 2.5,
              "&:hover": {
                borderColor: "#98A2B3",
                bgcolor: "#F9FAFB",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            disabled={isSaving}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 2.5,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default IncidentDetailsModal;